import { effectivePrice } from "@/lib/catalog";

export const REWARD_CATEGORY = "Wash n Fold";
export const PRESS_CATEGORY = "Press";
export const SHOE_CATEGORY = "Shoe & Accessories Clean";
export const TAILORING_CATEGORY = "Tailoring";
export const DRY_WASH_CATEGORIES = new Set(["Dry Clean", "Wash n Fold"]);
export const TAILORING_MULT = { standard: 1, t5_7: 1.5, t3_5: 2.5, t1: 7.5, t6_8h: 10 };

export function haversineKm(lat1, lng1, lat2, lng2) {
  const r = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(a)));
}

// Mirrors the mobile booking.tsx preview math (lines 204-385). The server is still the source
// of truth at creation; this drives the live review preview only.
export function computePricing(opts) {
  const {
    services = [],
    shop,
    cart = {},
    mode = "pickup",
    location = null,
    deliverySpeed = "standard",
    tailoringSpeed = "standard",
    loyalty = null,
    promo = null,
    earlybird = null,
    redeemPoints = false,
    redeemPointsAmount = null,
    redeemGamePoints = false,
    redeemMilestonePoints = false,
    useReward = false,
    useFreeWash = false,
    referral = null,
    selectedSlotAvailability = null,
  } = opts;

  const tmult = TAILORING_MULT[tailoringSpeed] ?? 1;
  const subtotalDryWash = services.reduce((s, sv) => (DRY_WASH_CATEGORIES.has(sv.category) ? s + (cart[sv.service_id] ?? 0) * effectivePrice(sv, shop) : s), 0);
  const tailoringBase = services.reduce((s, sv) => (sv.category === TAILORING_CATEGORY ? s + (cart[sv.service_id] ?? 0) * sv.price : s), 0);
  const subtotalOther = services.reduce((s, sv) => (!DRY_WASH_CATEGORIES.has(sv.category) && sv.category !== TAILORING_CATEGORY ? s + (cart[sv.service_id] ?? 0) * effectivePrice(sv, shop) : s), 0);
  const subtotalTailoring = Math.round(tailoringBase * tmult * 100) / 100;
  const dryWashSubtotal = subtotalDryWash;
  const subtotal = subtotalDryWash + subtotalTailoring + subtotalOther;

  const cartServices = services.filter((s) => (cart[s.service_id] ?? 0) > 0);
  const categorySubtotals = {};
  const categoryQtys = {};
  for (const s of cartServices) {
    const qty = cart[s.service_id] ?? 0;
    const price = effectivePrice(s, shop) * (s.category === TAILORING_CATEGORY ? tmult : 1);
    categorySubtotals[s.category] = (categorySubtotals[s.category] ?? 0) + price * qty;
    categoryQtys[s.category] = (categoryQtys[s.category] ?? 0) + qty;
  }

  const distanceKm =
    mode === "pickup" && location && shop?.shop_lat != null && shop?.shop_lng != null
      ? Math.round(haversineKm(shop.shop_lat, shop.shop_lng, location.lat, location.lng) * 100) / 100
      : null;
  const pickupMaxKm = shop?.pickup_max_distance_km ?? 12;
  const pickupTooFar = mode === "pickup" && distanceKm != null && distanceKm > pickupMaxKm;
  const distanceFee = (() => {
    if (distanceKm == null || !shop) return 0;
    const freeKm = shop.pickup_free_radius_km ?? 3;
    if (distanceKm <= freeKm) return 0;
    const slabKm = shop.pickup_distance_slab_km || 1;
    const slabFee = shop.pickup_distance_slab_fee ?? 0;
    const slabs = Math.ceil(Math.round(((distanceKm - freeKm) / slabKm) * 1e6) / 1e6);
    return slabs * slabFee;
  })();
  const deliveryFee = (() => {
    if (mode !== "pickup" || subtotal <= 0 || !shop) return 0;
    const base = Number(shop.pickup_base_fee || 0);
    const upto = Number(shop.pickup_base_upto || 0);
    const stepAmt = Number(shop.pickup_step_amount || 100) || 100;
    const stepFee = Number(shop.pickup_step_fee || 0);
    const slab = subtotal <= upto ? base : base + Math.ceil(Math.round(((subtotal - upto) / stepAmt) * 1e6) / 1e6) * stepFee;
    return slab + (pickupTooFar ? 0 : distanceFee);
  })();
  const expressFee = (() => {
    if (deliverySpeed === "standard" || dryWashSubtotal <= 0 || !shop) return 0;
    const feeKey = deliverySpeed === "priority" ? "priority_fee" : deliverySpeed === "express" ? "express_fee" : "rapid_fee";
    const amtKey = deliverySpeed === "priority" ? "priority_per_amount" : deliverySpeed === "express" ? "express_per_amount" : "rapid_per_amount";
    return shop[feeKey] * Math.max(1, Math.ceil(dryWashSubtotal / shop[amtKey]));
  })();

  const kgItems = cartServices.filter((s) => s.unit === "per kg");
  const totalKg = kgItems.reduce((n, s) => n + (cart[s.service_id] ?? 0), 0);
  const hasPress = cartServices.some((s) => s.category === PRESS_CATEGORY);
  const freeKgAvailable = loyalty
    ? Math.max(loyalty.free_wash_kg, totalKg >= (shop?.bulk_threshold_kg ?? 4) && !loyalty.free_wash_used_this_week ? shop?.bulk_free_kg ?? 1 : 0)
    : 0;
  const canUseFreeWash = freeKgAvailable > 0 && kgItems.length > 0 && (totalKg > 2 || hasPress);

  const gamePointsMinOrder = shop?.game_points_min_order ?? 1000;
  const bonusPointsAvailable = loyalty?.bonus_points ?? 0;
  const milestonePointsAvailable = loyalty?.milestone_points ?? 0;
  const categoryRedeemPct = shop?.category_redeem_pct ?? {};
  const categoryRedeemCap = (key) =>
    Object.entries(categorySubtotals).reduce((total, [cat, amt]) => total + (amt * (categoryRedeemPct[cat]?.[key] ?? 40)) / 100, 0);
  const gamePointsCapAmt = categoryRedeemCap("game_pct");
  const regularCapAmt = categoryRedeemCap("regular_pct");
  const regularPoints = loyalty ? Math.max(0, loyalty.points - loyalty.bonus_points) : 0;
  const valuePerPoint = loyalty?.redeem_value_per_point ?? 1;
  const canRedeemPoints = !!loyalty && regularPoints >= (loyalty.points_threshold ?? 1) && regularCapAmt > 0;
  const canRedeemGamePoints = !!loyalty && bonusPointsAvailable > 0 && subtotal >= gamePointsMinOrder && gamePointsCapAmt > 0;
  const canRedeemMilestonePoints = !!loyalty && milestonePointsAvailable > 0 && regularCapAmt > 0;
  const rewardItem = services
    .filter((s) => s.category === REWARD_CATEGORY && s.unit === "per piece" && (cart[s.service_id] ?? 0) > 0)
    .sort((a, b) => effectivePrice(a, shop) - effectivePrice(b, shop))[0];
  const canUseReward = !!loyalty && loyalty.rewards_available > 0 && !!rewardItem;

  const slotSurcharge =
    mode === "pickup" && selectedSlotAvailability?.is_early_late && selectedSlotAvailability.markup_percent != null
      ? Math.round(deliveryFee * (selectedSlotAvailability.markup_percent / 100) * 100) / 100
      : 0;

  const earlybirdCategoryRules = shop?.earlybird_category_rules ?? {};
  const rawPromoDiscount = promo?.discount ?? 0;
  const earlybirdCandidate =
    earlybird && earlybird.remaining > 0
      ? Math.round(
          Math.min(
            Object.entries(categorySubtotals).reduce((sum, [cat, amt]) => {
              const rule = earlybirdCategoryRules[cat];
              return rule?.enabled ? sum + (amt * (rule.percent ?? 0)) / 100 : sum;
            }, 0),
            shop?.earlybird_discount_cap ?? 200,
          ) * 100,
        ) / 100
      : 0;
  const earlybirdDiscount = earlybirdCandidate > rawPromoDiscount ? earlybirdCandidate : 0;
  const promoDiscount = earlybirdDiscount > 0 ? 0 : rawPromoDiscount;

  const maxPointsDiscount =
    redeemPoints && canRedeemPoints
      ? Math.floor(Math.min(regularPoints * valuePerPoint, regularCapAmt, Math.max(subtotal - promoDiscount - earlybirdDiscount, 0)) / valuePerPoint) * valuePerPoint
      : 0;
  const maxRedeemablePoints = valuePerPoint > 0 ? Math.round(maxPointsDiscount / valuePerPoint) : 0;
  // The customer can choose to spend fewer than the max usable points; null = spend the max.
  const pointsRedeemedCount =
    redeemPoints && canRedeemPoints
      ? redeemPointsAmount == null
        ? maxRedeemablePoints
        : Math.max(0, Math.min(maxRedeemablePoints, Math.round(redeemPointsAmount)))
      : 0;
  const pointsDiscount = pointsRedeemedCount * valuePerPoint;
  const gamePointsDiscount =
    redeemGamePoints && canRedeemGamePoints
      ? Math.min(bonusPointsAvailable, Math.floor(gamePointsCapAmt), Math.max(subtotal - promoDiscount - earlybirdDiscount - pointsDiscount, 0))
      : 0;
  const milestonePointsDiscount =
    redeemMilestonePoints && canRedeemMilestonePoints
      ? Math.min(
          milestonePointsAvailable,
          Math.max(regularCapAmt - pointsDiscount, 0),
          Math.max(subtotal - promoDiscount - earlybirdDiscount - pointsDiscount - gamePointsDiscount, 0),
        )
      : 0;
  const rewardDiscount =
    useReward && canUseReward
      ? Math.min(
          Math.min(effectivePrice(rewardItem, shop), loyalty?.punch_card_max_value ?? effectivePrice(rewardItem, shop)),
          Math.max(subtotal - promoDiscount - earlybirdDiscount - pointsDiscount - gamePointsDiscount - milestonePointsDiscount, 0),
        )
      : 0;

  const weightKgRate = totalKg > 0 ? kgItems.reduce((sum, s) => sum + (cart[s.service_id] ?? 0) * effectivePrice(s, shop), 0) / totalKg : 0;
  const weightFreeKgEntitled = useFreeWash && canUseFreeWash ? Math.min(freeKgAvailable, totalKg) : 0;
  const weightBilledKg = totalKg > 0 ? Math.max(totalKg - weightFreeKgEntitled, 3) : 0;
  const weightDeltaKg = Math.round((totalKg - weightBilledKg) * 1000) / 1000;
  const freeWashDiscount =
    weightDeltaKg > 0
      ? Math.min(
          Math.round(weightDeltaKg * weightKgRate * 100) / 100,
          Math.max(subtotal - promoDiscount - earlybirdDiscount - pointsDiscount - gamePointsDiscount - milestonePointsDiscount - rewardDiscount, 0),
        )
      : 0;
  const weightMinTopup = weightDeltaKg < 0 ? Math.round(-weightDeltaKg * weightKgRate * 100) / 100 : 0;

  const welcomeBase = Math.max(
    subtotal - promoDiscount - earlybirdDiscount - pointsDiscount - gamePointsDiscount - milestonePointsDiscount - rewardDiscount - freeWashDiscount,
    0,
  );
  const welcomePercent = referral?.welcome_eligible ? referral.welcome_percent : loyalty?.welcome_offer_active ? loyalty.welcome_offer_percent : 0;
  const welcomeCap = referral?.welcome_eligible ? referral.welcome_cap : shop?.welcome_offer_cap ?? 100;
  const welcomeDiscount = welcomePercent > 0 ? Math.min(welcomeBase * (welcomePercent / 100), welcomeCap) : 0;

  const grandTotal =
    Math.max(
      subtotal - promoDiscount - earlybirdDiscount - pointsDiscount - gamePointsDiscount - milestonePointsDiscount - rewardDiscount - freeWashDiscount - welcomeDiscount,
      0,
    ) +
    deliveryFee +
    expressFee +
    slotSurcharge +
    weightMinTopup;

  const itemCount = Object.values(cart).reduce((a, b) => a + b, 0);

  // Home pickup minimum — post-discount goods value (excluding fees) must clear the threshold.
  const totalDiscounts = promoDiscount + earlybirdDiscount + pointsDiscount + gamePointsDiscount + milestonePointsDiscount + rewardDiscount + welcomeDiscount + freeWashDiscount;
  const discountedGoods = Math.max(Math.round((subtotal - totalDiscounts) * 100) / 100, 0);
  const pickupMinOrder = Number(shop?.pickup_min_order_value || 0);
  const pickupBelowMin = mode === "pickup" && subtotal > 0 && pickupMinOrder > 0 && discountedGoods < pickupMinOrder;

  // Shoe & Accessories minimum pairs (both modes).
  const shoeMinPairs = Number(shop?.shoe_min_pairs || 0);
  const shoeWaiverValue = Number(shop?.shoe_min_waiver_value || 0);
  const shoeQty = categoryQtys[SHOE_CATEGORY] ?? 0;
  const hasOtherItems = Object.entries(categoryQtys).some(([c, q]) => q > 0 && c !== SHOE_CATEGORY);
  const shoeWaived = shoeWaiverValue > 0 && hasOtherItems && subtotal > shoeWaiverValue;
  // Shoe minimum gates PICKUP only, and is waived for a qualifying mixed pickup above the value.
  const shoeBelowMin = mode === "pickup" && shoeMinPairs > 0 && shoeQty > 0 && shoeQty < shoeMinPairs && !shoeWaived;

  // Game-points visibility helpers (shown in Review even when not yet redeemable, with a reason).
  const hasGamePoints = bonusPointsAvailable > 0;
  const gamePointsCoverage = Math.min(bonusPointsAvailable, Math.floor(gamePointsCapAmt || 0));

  // "You'll earn" preview — floor(final payable total ÷ ₹-per-point); mirrors mobile, credited on completion.
  const rupeesPerPoint = Number(shop?.loyalty_rupees_per_point || loyalty?.rupees_per_point || 10) || 10;
  const earnedPointsPreview = Math.floor(grandTotal / rupeesPerPoint);

  return {
    subtotal, dryWashSubtotal, subtotalTailoring, itemCount, cartServices, categorySubtotals, categoryQtys,
    deliveryFee, expressFee, distanceKm, pickupTooFar, pickupMaxKm, slotSurcharge,
    promoDiscount, earlybirdDiscount, pointsDiscount, gamePointsDiscount, milestonePointsDiscount,
    rewardDiscount, rewardItem, freeWashDiscount, weightMinTopup, welcomeDiscount, welcomePercent,
    canRedeemPoints, canRedeemGamePoints, canRedeemMilestonePoints, canUseReward, canUseFreeWash,
    regularPoints, pointsRedeemMin: loyalty?.points_threshold ?? 1,
    maxRedeemablePoints, pointsRedeemedCount, valuePerPoint,
    bonusPointsAvailable, milestonePointsAvailable, totalKg, hasTailoring: cartServices.some((s) => s.category === TAILORING_CATEGORY),
    pickupBelowMin, pickupMinOrder, discountedGoods, pickupBaseFee: Number(shop?.pickup_base_fee || 0),
    shoeMinPairs, shoeQty, shoeBelowMin, shoeWaiverValue,
    hasGamePoints, gamePointsMinOrder, gamePointsCapAmt, gamePointsCoverage,
    rupeesPerPoint, earnedPointsPreview,
    grandTotal,
  };
}
